/**
 * HamburgerMenu
 * ハンバーガーメニューの開閉制御とアクセシビリティ対応
 *
 * @usage
 * HTML: <button class="js-hamburger" aria-controls="nav-menu" aria-expanded="false">メニュー</button>
 * JS: new HamburgerMenu();
 */
export default class HamburgerMenu {
  constructor(options = {}) {
    this.options = {
      trigger: '.js-hamburger',
      menu: '.js-nav-menu',
      activeClass: 'is-active',
      menuOpenClass: 'is-menu-open',
      ...options
    };

    this.trigger = document.querySelector(this.options.trigger);
    this.menu = document.querySelector(this.options.menu);
    this.isOpen = false;

    if (this.trigger) {
      this.init();
    } else {
      console.warn('HamburgerMenu: trigger element not found');
    }
  }

  init() {
    this.bindEvents();
    this.setupAccessibility();
  }

  bindEvents() {
    // メニューボタンのクリックイベント
    this.trigger.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggle();
    });

    // Escapeキーでメニューを閉じる
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // メニュー外クリックで閉じる
    document.addEventListener('click', (e) => {
      if (this.isOpen && !this.trigger.contains(e.target) && !this.menu?.contains(e.target)) {
        this.close();
      }
    });

    // ウィンドウリサイズ時の処理
    window.addEventListener('resize', () => {
      if (this.isOpen && window.innerWidth >= 768) {
        this.close();
      }
    });

    // 検索フォームが開いた時の処理
    document.addEventListener('searchform:opened', () => {
      if (this.isOpen) {
        this.close();
        // 検索フォームが開いた後、スクロールロックを再実行
        setTimeout(() => {
          document.body.style.overflow = 'hidden';
        }, 325);
      }
    });

    // 検索フォームトリガーボタンのクリックで閉じる
    const searchTrigger = document.querySelector('.js-searchform-trigger');
    if (searchTrigger) {
      searchTrigger.addEventListener('click', () => {
        if (this.isOpen) {
          this.close();
          // 検索フォームが開く際のスクロールロック再実行
          setTimeout(() => {
            document.body.style.overflow = 'hidden';
          }, 350);
        }
      });
    }
  }

  setupAccessibility() {
    // ARIA属性の初期化
    if (this.menu) {
      this.menu.setAttribute('role', 'navigation');
      this.menu.setAttribute('aria-hidden', 'true');
    }

    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('role', 'button');

    // 初期状態でフォーカス可能な要素を無効化
    this.setMenuFocusability(false);

    // キーボードナビゲーションの設定
    if (this.menu) {
      const menuLinks = this.menu.querySelectorAll('a, button');
      menuLinks.forEach((link, index) => {
        link.addEventListener('keydown', (e) => {
          if (e.key === 'Tab' && e.shiftKey && index === 0) {
            // 最初のリンクでShift+Tabを押したらトリガーにフォーカス
            e.preventDefault();
            this.trigger.focus();
          } else if (e.key === 'Tab' && !e.shiftKey && index === menuLinks.length - 1) {
            // 最後のリンクでTabを押したらメニューを閉じる
            this.close();
          }
        });
      });
    }
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (this.isOpen) return;

    this.isOpen = true;

    // クラスの追加
    document.body.classList.add(this.options.menuOpenClass);
    this.trigger.classList.add(this.options.activeClass);
    this.menu?.classList.add(this.options.activeClass);

    // ARIA属性の更新
    this.trigger.setAttribute('aria-expanded', 'true');
    this.menu?.setAttribute('aria-hidden', 'false');

    // aria-hidden="false"の時はフォーカス可能な要素を有効化
    this.setMenuFocusability(true);

    // ボディスクロールをロック
    this.lockBodyScroll();

    // 最初のメニューリンクにフォーカス（オプション）
    const firstLink = this.menu?.querySelector('a, button');
    if (firstLink) {
      setTimeout(() => firstLink.focus(), 100);
    }

    // カスタムイベントの発火
    this.trigger.dispatchEvent(new CustomEvent('hamburger:opened', {
      detail: { menu: this }
    }));
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;

    // クラスの削除
    this.trigger.classList.remove(this.options.activeClass);
    this.menu?.classList.remove(this.options.activeClass);
    document.body.classList.remove(this.options.menuOpenClass);

    // ARIA属性の更新
    this.trigger.setAttribute('aria-expanded', 'false');
    this.menu?.setAttribute('aria-hidden', 'true');

    // aria-hidden="true"の時はフォーカス可能な要素を無効化
    this.setMenuFocusability(false);

    // ボディスクロールのロック解除
    this.unlockBodyScroll();

    // フォーカスをトリガーに戻す
    this.trigger.focus();

    // カスタムイベントの発火
    this.trigger.dispatchEvent(new CustomEvent('hamburger:closed', {
      detail: { menu: this }
    }));
  }

  lockBodyScroll() {
    // ボディのスクロールを無効化
    document.body.style.overflow = 'hidden';
  }

  unlockBodyScroll() {
    // ボディのスクロールを復元
    document.body.style.removeProperty('overflow');
  }

  setMenuFocusability(enabled) {
    // メニュー内のフォーカス可能な要素の有効/無効を切り替え
    if (!this.menu) return;

    const focusableElements = this.menu.querySelectorAll(
      'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    focusableElements.forEach(element => {
      if (enabled) {
        // 元のtabindexを復元
        const originalTabindex = element.dataset.originalTabindex;
        if (originalTabindex !== undefined) {
          if (originalTabindex === 'null') {
            element.removeAttribute('tabindex');
          } else {
            element.setAttribute('tabindex', originalTabindex);
          }
          delete element.dataset.originalTabindex;
        }
      } else {
        // 現在のtabindexを保存してからtabindex="-1"に設定
        const currentTabindex = element.getAttribute('tabindex');
        element.dataset.originalTabindex = currentTabindex || 'null';
        element.setAttribute('tabindex', '-1');
      }
    });
  }

  destroy() {
    // イベントリスナーの削除とクラスのリセット
    this.close();

    // 追加したクラスや属性を削除
    this.trigger.classList.remove(this.options.activeClass);
    this.trigger.removeAttribute('aria-expanded');
    this.trigger.removeAttribute('role');

    if (this.menu) {
      this.menu.classList.remove(this.options.activeClass);
      this.menu.removeAttribute('aria-hidden');
      this.menu.removeAttribute('role');
    }

    document.body.classList.remove(this.options.menuOpenClass);
  }
}
